import { useEffect, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Panel, Button, Field, FormRow, Select } from '../../ds/index'
import { api, post } from '../../lib/api'
import { useInstallBrand } from '../../lib/session'
import { HandpickedTree } from '../../components/BrandMark'
import { Gift } from 'lucide-react'
import { Icon } from '../../components/Icon'
import { dollars } from '../../lib/format'

type JoinInfo = {
  org: { name: string; slug: string }; open: boolean; closed_reason: string | null; categories: { id: string; name: string }[]
  areas: { id: string; name: string }[]; min_password: number
  offer?: { min_cents: number; gift_cents: number; trial_days: number }
}

function Frame({ children }: { children: React.ReactNode }) {
  const brand = useInstallBrand()
  return (
    <div className="login">
      <Panel className="login-card join-card">
        {brand === 'handpicked' && (
          <div className="hp-login-mark"><HandpickedTree size={72} /><span className="hp-wordmark">Handpicked</span></div>
        )}
        {children}
        <div className="powered">Powered by SPEC Systems</div>
      </Panel>
    </div>
  )
}

/**
 * Sign up, step 1 of 2, signed out, built for a phone at an event: name, email,
 * mobile, business and trade ("Other" takes it typed). No password (a link to
 * set one is emailed; they stay signed in) and every area by default. It makes
 * the login; step 2 is the deposit (Signup.tsx). The rest is finished later.
 */
export default function JoinStart() {
  const { slug: given } = useParams()
  const [slug, setSlug] = useState<string | null>(given || null)
  const [info, setInfo] = useState<JoinInfo | null>(null)
  const [gone, setGone] = useState<string | null>(null)
  const [f, setF] = useState({ name: '', email: '', phone: '', business_name: '', category_id: '', other_trade: '' })
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value })

  useEffect(() => {
    if (slug) return
    api<{ slug: string }>('/api/join').then((r) => setSlug(r.slug)).catch((e: Error) => setGone(e.message))
  }, [slug])
  useEffect(() => {
    if (!slug) return
    api<JoinInfo>(`/api/join/${encodeURIComponent(slug)}`)
      .then((r) => { setInfo(r); document.title = `Sign up with ${r.org.name}` })
      .catch((e: Error) => setGone(e.message))
  }, [slug])

  const other = f.category_id === OTHER
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      await post(`/api/join/${encodeURIComponent(slug!)}/account`, {
        name: f.name, email: f.email, phone: f.phone, business_name: f.business_name,
        ...(other ? { other_trade: f.other_trade } : { category_id: f.category_id }),
      })
      window.location.assign('/portal')
    } catch (e2) { setErr((e2 as Error).message); setBusy(false) }
  }

  if (gone) return <Frame><div className="login-mark">Sign up</div><p className="login-sub">{gone}</p><Link className="btn btn-secondary" to="/">Sign in</Link></Frame>
  if (!info) return <Frame><div aria-busy="true" style={{ minHeight: 240 }} /></Frame>
  if (!info.open) return <Frame><div className="login-mark">Sign up with {info.org.name}</div><p className="login-sub">{info.closed_reason}</p><Link className="btn btn-secondary" to="/">Sign in</Link></Frame>

  const ready = /\S+\s+\S+/.test(f.name.trim()) && /\S+@\S+\.\S+/.test(f.email) && f.phone.replace(/\D/g, '').length >= 10 && f.business_name.trim().length >= 2
    && (other ? f.other_trade.trim().length >= 2 : Boolean(f.category_id))
  return (
    <Frame>
      <div className="login-mark">Get leads from {info.org.name}</div>
      <p className="login-sub">Two quick steps: you and your business, then your first tab{info.offer ? ` of ${dollars(info.offer.min_cents)} or more` : ''}. You can add the rest later.</p>
      {info.offer && info.offer.gift_cents > 0 && (
        <p className="gift-note"><Icon of={Gift} size={18} /><span><strong>{dollars(info.offer.gift_cents)} sign-up gift</strong> added to your first tab</span></p>
      )}
      <form onSubmit={submit} className="stack">
        <Field label="Your full name" id="j-name" autoComplete="name" autoCapitalize="words" enterKeyHint="next" value={f.name} onChange={set('name')} />
        <Field label="Email" id="j-email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} enterKeyHint="next" value={f.email} onChange={set('email')} />
        <Field label="Mobile number" id="j-phone" type="tel" inputMode="tel" autoComplete="tel" enterKeyHint="next" value={f.phone} onChange={set('phone')} />
        <Field label="Business name" id="j-biz" autoComplete="organization" autoCapitalize="words" enterKeyHint="next" value={f.business_name} onChange={set('business_name')} />
        <FormRow label="Your trade" htmlFor="j-trade">
          <Select id="j-trade" value={f.category_id} onChange={set('category_id')}>
            <option value="">Choose one</option>
            {info.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            <option value={OTHER}>Other (type it in)</option>
          </Select>
        </FormRow>
        {other && <Field label="What do you do?" id="j-other" placeholder="For example: pool cleaning" autoCapitalize="sentences" enterKeyHint="go" value={f.other_trade} onChange={set('other_trade')} />}
        {info.areas.length > 0 && <p className="form-help" style={{ margin: 0 }}>You will get leads from {areaSummary(info.areas)}. You can narrow that once you are in.</p>}
        {err && <p className="form-error" role="alert">{err}</p>}
        <Button type="submit" disabled={busy || !ready}>{busy ? 'One moment' : 'Continue'}</Button>
        <p className="muted" style={{ margin: 0, fontSize: 13, textAlign: 'center' }}>Already signed up? <Link to="/">Sign in</Link></p>
      </form>
    </Frame>
  )
}

const OTHER = '__other__'
/** "all 13 areas we cover" reads better than thirteen names on a phone. */
const areaSummary = (areas: { name: string }[]) => areas.length > 4 ? `all ${areas.length} areas we cover` : areas.map((a) => a.name).join(', ')

/** Where the confirm link lands. */
export function ConfirmEmail() {
  const { token = '' } = useParams()
  const [state, setState] = useState<'working' | 'done' | string>('working')
  useEffect(() => {
    post(`/api/auth/confirm/${encodeURIComponent(token)}`, {}).then(() => setState('done')).catch((e: Error) => setState(e.message))
  }, [token])
  return (
    <Frame>
      <div className="login-mark">{state === 'done' ? 'Email confirmed' : state === 'working' ? 'Confirming' : 'Link expired'}</div>
      <p className="login-sub">{state === 'done' ? 'Thank you. Carry on with your sign-up where you left off.' : state === 'working' ? '' : state}</p>
      {state !== 'working' && <a className="btn btn-primary" href="/portal">Continue</a>}
    </Frame>
  )
}
